import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ScratchCardTracked from './ScratchCardTracked';
import { trackEvent } from './TrackingProvider';
import { useT, useLang, localizedPath, DEFAULT_LANG } from '../i18n';
import { API_BASE } from '../utils/apiBase';

// API_BASE = https://joli-mot.fr en production ; REACT_APP_API_BASE le change seulement pour les essais en local.
const API_BASE_URL = `${API_BASE}/apiEtsy.php`;
const API_KEY = '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75';
// Prix « à partir de » du bandeau de remerciement (affiché avec le format monétaire de la langue).
const FROM_PRICE = 2.2;

const ScratchCardTrackedPage = () => {
  // Langue : ?lang=xx du lien, sinon langue du navigateur, sinon français (décidé au démarrage par le socle ;
  // l'adresse ne change pas). Le message de la carte n'est jamais traduit.
  const t = useT('scratch-card');
  const { lang } = useLang();
  const { id: rawId } = useParams();
  // Extraire l'ID avant le & (format: "1037&11990315859116" -> "1037")
  const id = rawId.split('&')[0];
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [titre, setTitre] = useState('');
  const [textSize, setTextSize] = useState(24);
  const [textColor, setTextColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(30);
  const [marginTop, setMarginTop] = useState(0);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [textHeight, setTextHeight] = useState(0);

  // État pour le tracking
  const [isScratched, setIsScratched] = useState(false);
  const [scratchInfo, setScratchInfo] = useState(null); // Info sur les grattages (count, max)
  const [isBlocked, setIsBlocked] = useState(false); // Carte bloquée si max atteint

  // Charger dynamiquement le CSS
  useEffect(() => {
    import('./ScratchCardOnlyPage.css');
  }, []);

  // Désactiver le scroll pendant le grattage (évite que le geste tactile fasse défiler la
  // page sur mobile), le réactiver une fois la carte révélée pour que le bandeau CTA en
  // dessous reste atteignable.
  useEffect(() => {
    document.body.style.overflow = isScratched ? 'auto' : 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isScratched]);

  // Charger les données de la carte ET vérifier le nombre de grattages
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        // 1. Récupérer les données de la carte
        const cardResponse = await fetch(
          `${API_BASE_URL}?cleApi=${API_KEY}&service=getCarte&id=${id}`
        );
        if (!cardResponse.ok) {
          throw new Error('Erreur lors du chargement de la carte');
        }
        const cardData = await cardResponse.json();

        setConfig(cardData);
        setMessage(decodeHtml(cardData.message || ''));
        setTitre(decodeHtml(cardData.titre || ''));
        setTextSize(cardData.taille_text || 24);
        setTextColor(cardData.couleur_text || '#000000');
        setBrushSize(30);
        setMarginTop(cardData.margin_top_gratte || 0);
        setLineHeight(cardData['line-height'] || 1.5);
        setTextHeight(0);

        // 2. Vérifier le nombre de grattages
        console.log('[ScratchCardTrackedPage] Vérification du nombre de grattages pour carte_id:', id);
        const scratchResponse = await fetch(
          `${API_BASE_URL}?cleApi=${API_KEY}&service=getCarteUse&carte_id=${id}`
        );
        console.log('[ScratchCardTrackedPage] Réponse getCarteUse status:', scratchResponse.status);
        
        if (scratchResponse.ok) {
          const scratchData = await scratchResponse.json();
          console.log('[ScratchCardTrackedPage] Données grattage:', scratchData);
          setScratchInfo(scratchData);
          
          // Vérifier si la carte est bloquée
          if (scratchData && scratchData.scratch_count >= scratchData.max_scratch_allowed) {
            setIsBlocked(true);
          }
        }

        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    fetchCardData();
  }, [id]);

  const decodeHtml = (html) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  // Callback quand la carte est grattée à 60%
  const handleScratchComplete = useCallback(async (percentage) => {
    if (isScratched || isBlocked) return;

    setIsScratched(true);
    console.log('[ScratchCardTrackedPage] Carte grattée à', percentage, '% - Enregistrement...');

    try {
      // cleApi et service en query string, données en body
      const response = await fetch(`${API_BASE_URL}?cleApi=${API_KEY}&service=incrementScratchCount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          carte_id: id,
          percentage: Math.round(percentage)
        })
      });

      console.log('[ScratchCardTrackedPage] Réponse incrementScratchCount status:', response.status);
      const responseText = await response.text();
      console.log('[ScratchCardTrackedPage] Réponse brute:', responseText);

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      const result = JSON.parse(responseText);
      console.log('[ScratchCardTrackedPage] Grattage enregistré:', result);

      // Mettre à jour l'info locale
      setScratchInfo(prev => ({
        ...prev,
        scratch_count: (prev?.scratch_count || 0) + 1
      }));

    } catch (err) {
      console.error('Erreur API:', err);
    }
  }, [id, isScratched, isBlocked]);

  // Lien vers la boutique : en français comme avant (lien du routeur). Dans une autre langue, adresse avec
  // préfixe (/de/…) : cette page n'a pas de préfixe, donc un vrai lien (la boutique se recharge dans la langue).
  const shopPath = '/cartes-a-gratter-personnalisees?utm_source=carte&utm_medium=cta';
  const shopLink = lang === DEFAULT_LANG
    ? { component: Link, to: shopPath }
    : { component: 'a', href: localizedPath(shopPath, lang) };

  if (loading) return <div className="scratch-card-only-page"><div>{t('scratch-card.loading')}</div></div>;
  if (error) return <div className="scratch-card-only-page"><div>{t('scratch-card.errorPrefix', { message: error.message })}</div></div>;

  // Affichage si la carte est bloquée
  if (isBlocked) {
    return (
      <div className="scratch-card-only-page">
        <Helmet>
          <title>{t('scratch-card.expired.pageTitle')}</title>
        </Helmet>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          textAlign: 'center',
          padding: '20px'
        }}>
          <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>
            🎫 {t('scratch-card.expired.title')}
          </h2>
          <p style={{ color: '#666', maxWidth: '400px' }}>
            {t('scratch-card.expired.text')}
          </p>
          <div className="sign" style={{ marginTop: '40px' }}>
            <span>{t('scratch-card.sign.label')}</span>
            <br />
            {t('scratch-card.sign.by')} <a href="https://joli-mot.fr/">joliMot.fr</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scratch-card-only-page">
      {/* Titre/description volontairement neutres : ce lien est partagé (SMS, WhatsApp, mail)
          et l'aperçu généré automatiquement ne doit pas dévoiler qu'il s'agit d'une carte à
          gratter — ce serait un indice qui gâche la surprise avant même l'ouverture du lien. */}
      <Helmet>
        <title>{`${t('scratch-card.surprise.title')} ✨ | Joli Ptit Mot`}</title>
        <meta
          name="description"
          content={t('scratch-card.surprise.description')}
        />
        <meta property="og:title" content={`${t('scratch-card.surprise.title')} ✨`} />
        <meta property="og:description" content={t('scratch-card.surprise.description')} />
        <meta property="og:image" content="https://joli-mot.fr/logo512.png" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <ScratchCardTracked
        width={400}
        height={400}
        gratteSrc={`/assets_react/grattes/${config.gratte}.png`}
        brushSize={brushSize}
        text={message}
        titre={titre}
        imageSrc={`/assets_react/visuels/${config.visuel}`}
        textSize={textSize}
        textColor={textColor}
        marginTop={marginTop}
        lineHeight={lineHeight}
        textHeight={textHeight}
        onScratchComplete={handleScratchComplete}
        ctaContent={
          // Remplace le cœur, au même endroit, une fois le message lu (cf. délai dans
          // ScratchCardTracked.js). Remerciement + petit mot + prix affiché tout de suite
          // (pas d'ambiguïté sur le coût) + invitation à en offrir une à un proche.
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.96)',
              borderRadius: 3,
              boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
              textAlign: 'center',
              px: 2.5,
              py: 2.5,
            }}
          >
            <Typography sx={{ fontSize: 20, mb: 0.5 }}>🎉</Typography>
            <Typography sx={{ fontWeight: 700, color: '#3a2b2b', fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 19, mb: 1 }}>
              {t('scratch-card.thanks.title')}
            </Typography>
            <Typography sx={{ color: '#7a6060', fontSize: 13.5, lineHeight: 1.5, mb: 1.5 }}>
              {t('scratch-card.thanks.smile')}
            </Typography>
            <Typography sx={{ color: '#3a2b2b', fontSize: 13.5, lineHeight: 1.5, mb: 1 }}>
              {t('scratch-card.thanks.offer')}
            </Typography>
            <Typography sx={{ color: '#8C8BC4', fontWeight: 700, fontSize: 15, mb: 1.5 }}>
              {t('scratch-card.thanks.from', { amount: FROM_PRICE })}
            </Typography>
            <Button
              {...shopLink}
              onClick={() => trackEvent('card_cta_click', { category: 'Acquisition', label: 'post_scratch_overlay' })}
              variant="contained"
              sx={{
                bgcolor: '#8C8BC4',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 5,
                textTransform: 'none',
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#7a79b0', boxShadow: 'none' },
              }}
            >
              {t('scratch-card.thanks.order')}
            </Button>
          </Box>
        }
      />

      <div className="sign">
        <span>{t('scratch-card.sign.label')}</span>
        <br />
        {t('scratch-card.sign.by')} <a href="https://joli-mot.fr/">joliMot.fr</a>
      </div>
    </div>
  );
};

export default ScratchCardTrackedPage;
